// src/store/Slice/AppVersion/AppVersionApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Panel base URL, e.g. https://panel.ramaapp.com/api/
// Trailing slashes are stripped so we can join paths safely.
const PANEL_API = (import.meta.env.VITE_PANEL_API || "").replace(/\/+$/, "");

// Backend (static-service) stores updateUrl as a relative path when the
// version was uploaded as a file (e.g. "statics/apps/app_169...apk"), and
// as a full absolute URL when it's an external link (Google Play/App
// Store/Bazaar/Myket). Consumers (download buttons, window.location.href,
// <a href>) always need a directly-usable absolute URL, so we normalize it
// once here instead of every page re-implementing the same prefix check.
const toAbsoluteFileUrl = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${PANEL_API}/${url.replace(/^\/+/, "")}`;
};

export const appVersionApi = createApi({
  reducerPath: "appVersionApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${PANEL_API}/statics`,
    prepareHeaders: (headers, { getState }) => {
      const token =
        getState()?.userSlice?.token || localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["AppVersion"],
  endpoints: (builder) => ({
    getAppVersion: builder.query({
      query: ({ currentVersion, appType }) => ({
        url: "/version",
        method: "GET",
        params: { currentVersion, appType },
      }),
      transformResponse: (response) => ({
        ...response,
        updateUrl: toAbsoluteFileUrl(response?.updateUrl),
      }),
      providesTags: ["AppVersion"],
    }),

    getLatestAppVersion: builder.query({
      query: (appType) => ({
        url: `/version/${appType === "provider" ? "provider" : "customer"}/latest`,
        method: "GET",
      }),
      transformResponse: (response) => ({
        ...response,
        data: response?.data
          ? { ...response.data, updateUrl: toAbsoluteFileUrl(response.data.updateUrl) }
          : response?.data,
      }),
      providesTags: ["AppVersion"],
    }),
  }),
});

export const {
  useGetAppVersionQuery,
  useLazyGetAppVersionQuery,
  useGetLatestAppVersionQuery,
  useLazyGetLatestAppVersionQuery,
} = appVersionApi;